import { Alert, Typography } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { IRecordFurtherEducationGroupNode, RecordFurtherEducationGroupNode } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordFurtherEducationGroupNode";
import { RecordListItemUserQualification } from "ui/Scripts/source/models/recordUserQualification/recordListItemUserQualification";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IRecordEditItemFurtherEducation } from "../../../models/record/recordEditItemFurtherEducation";
import { IRecordEditItemQualification, RecordEditItemQualification, RecordEditItemQualificationSave } from "../../../models/record/recordEditItemQualification";
import * as recordService from "../../../models/services/recordService";
import { StoreUtils } from "../../../models/store/storeUtils";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { SelectQualificationDialog } from "../SelectQualificationDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface IRecordEditQualificationListProp {
    isReadOnly: boolean;
    companyId: string;
    recordId: string;
    userCode: string;
    recordFurtherEducations: IRecordEditItemFurtherEducation[];
    recordQualifications: IRecordEditItemQualification[];
    furtherEducationGroups: IRecordFurtherEducationGroupNode[];
    onRemoved: (ids: string[]) => void;
    onSaved: (qualification: IRecordEditItemQualification) => void;
}

interface IRecordEditQualificationListState {
    // List
    isLoading: boolean;
    recordQualifications: IRecordEditItemQualification[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];
    selectedItemHasPrevious: boolean;
    selectedItemHasNext: boolean;

    // Dialog
    showSelectQualificationDialog: boolean;
    selectQualificationDialogFurtherEducations: IIdTitleNode[];
}

export class RecordEditQualificationList extends React.Component<IRecordEditQualificationListProp, IRecordEditQualificationListState> {
    getFilteredList = (filter: string, column: string, asc: boolean): IRecordEditItemQualification[] => {
        const props = this.props;
        let result = props.recordQualifications ? props.recordQualifications.slice(0) : [];
        RecordEditItemQualification.setFurtherEducationCodes(result, props.furtherEducationGroups, props.recordFurtherEducations);
        if (filter) {
            const lowFilter = filter.toLowerCase();
            result = result.filter(i => i.code.toLowerCase().indexOf(lowFilter) > -1);
        }
        RecordEditItemQualification.sortRecordEditItemFurtherEducations(result, column, asc);
        return result;
    };

    constructor(props: IRecordEditQualificationListProp) {
        super(props);
        const filter = "";
        const sortColumn = "code";
        const sortOrderIsAsc = true;
        this.state = {
            isLoading: false,
            recordQualifications: this.getFilteredList(filter, sortColumn, sortOrderIsAsc),
            filter,
            sortColumn,
            sortOrderIsAsc,
            selectedId: null,
            checkedIds: [],
            selectedItemHasPrevious: false,
            selectedItemHasNext: false,
            showSelectQualificationDialog: false,
            selectQualificationDialogFurtherEducations: []
        };
    }

    filteredList = (filter: string, sortColumn: string, sortOrderIsAsc: boolean) => {
        this.setState({
            filter,
            sortColumn,
            sortOrderIsAsc,
            recordQualifications: this.getFilteredList(filter, sortColumn, sortOrderIsAsc)
        });
    };

    componentDidUpdate(prevProps: IRecordEditQualificationListProp, prevState: IRecordEditQualificationListState) {
        const props = this.props;
        const state = this.state;
        if (EditItem.getHash(prevProps.recordQualifications) === EditItem.getHash(props.recordQualifications)) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    };

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: IRecordEditQualificationListProp, qualificationIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("ids", qualificationIds);
        return result;
    };

    handleRemove = async () => {
        const obj = this;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const recordQualification = state.recordQualifications.find(i => i.id === selectedIds[0]);
        if (!recordQualification) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRecordQualificationParameter, recordQualification.code), false) === ConfirmationDialogResult.Yes) {
            const saveData = this.getRemoveSaveData(this.props, selectedIds);
            // Call server
            const result = await AppUtils.callService(() => recordService.recordEditRemoveQualifications(saveData.obj));
            if (!result) return;
            obj.props.onRemoved(selectedIds);
        }
    };
    
    handleAdd = () => {
        const props = this.props;
        const selectQualificationDialogFurtherEducations = RecordFurtherEducationGroupNode.getFurtherEducationsForQualification(props.furtherEducationGroups);
        const reservedNodes = IdTitleNode.getByIds(selectQualificationDialogFurtherEducations, props.recordQualifications.map(i => i.furtherEducationId ? i.furtherEducationId : i.furtherEducationGroupId));
        for (const reservedNode of reservedNodes) {
            reservedNode.active = false;
        }
        this.setState({
            showSelectQualificationDialog: true,
            selectQualificationDialogFurtherEducations
        });
    };

    getSaveData = (props: IRecordEditQualificationListProp, qualification: RecordEditItemQualificationSave): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("qualification", qualification);
        return result;
    };

    saveToDb = async (qualification: IRecordEditItemQualification): Promise<RecordEditItemQualification> => {
        const saveData = this.getSaveData(this.props, new RecordEditItemQualificationSave(qualification));
        // Call server
        const result = await AppUtils.callService(() => recordService.recordEditSaveQualification(saveData.obj));
        if (!result) return null;
        qualification.id = result.id;
        qualification.rowId = result.rowId;
        qualification.modifyUser = StoreUtils.getNowModifyUser();
        return qualification;
    };

    onSelectQualificationDialogOk = async (ids: string[]) => {
        const props = this.props;
        if (!ids || ids.length < 1) return;
        const item = RecordFurtherEducationGroupNode.getItemById(props.furtherEducationGroups, ids[0]);
        if (!item) return;
        const isGroup = RecordFurtherEducationGroupNode.isGroup(item);
        const result = new RecordEditItemQualification();
        result.id = Base.getGuid();
        result.furtherEducationId = !isGroup ? item.id : "";
        result.furtherEducationGroupId = isGroup ? item.id : "";
        result.setCodeAndValidity(props.furtherEducationGroups, props.recordFurtherEducations);
        await this.saveToDb(result);
        if (!result) return;
        this.props.onSaved(result);
        this.setState({
            showSelectQualificationDialog: false
        });
    };

    onSelectQualificationDialogCancel = () => {
        this.setState({
            showSelectQualificationDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <VerticalContainer
                    before={<>
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter}
                                />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddQualification}
                                    removeTooltip={Translations.RemoveQualification}
                                    addDisabled={props.isReadOnly}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                        {!!props.isReadOnly &&
                            <Alert severity="info" sx={{ marginBottom: "1rem" }}>{Translations.NewRecordUserMustBeSavedBeforeQualificationsCanBeAdded}</Alert>
                        }
                    </>}
                    main={
                        <>
                            {!props.isReadOnly &&
                                <List
                                    intialized={true}
                                    isLoading={state.isLoading}
                                    hasMore={false}
                                    globalLoadingInUse={false}
                                    height="100%"
                                    header={{
                                        columns: [
                                            { size: 8, column: "code", content: Translations.Education + " / " + Translations.Competency }, // "col-xl-6 col-8"
                                            { size: 2, column: "validity", content: Translations.Validity }, // "col-xl-3 col-2"
                                            { size: 2, column: "validityEndDate", content: Translations.ValidityEndDate } // "col-xl-3 col-2"
                                        ]
                                    }}
                                    lines={
                                        state.recordQualifications.map((recordQualification) => {
                                            return {
                                                columns: [
                                                    { size: 8, content: recordQualification.code },
                                                    {
                                                        size: 2,
                                                        content: (
                                                            <Typography
                                                                sx={{
                                                                    color: (recordQualification.furtherEducationGroupId && recordQualification.validUserFurtherEducations.length >= recordQualification.requiredQualificationCount ||
                                                                        !recordQualification.furtherEducationGroupId && recordQualification.validUserFurtherEducations.length > 0
                                                                        ? "success.main"
                                                                        : "error.main")
                                                                }}
                                                            >
                                                                {RecordListItemUserQualification.getQualificationState(recordQualification)}
                                                            </Typography>
                                                        ),
                                                    },
                                                    { size: 2, content: Base.utcTimeToDateStr(recordQualification.validityEndDate) }
                                                ],
                                                id: recordQualification.id
                                            };
                                        })
                                    }
                                    noDataMessage={Translations.AddQualifications}
                                    sortColumn={state.sortColumn}
                                    sortOrderIsAsc={state.sortOrderIsAsc}
                                    selectedId={state.selectedId}
                                    checkedIds={state.checkedIds}
                                    onSetSelectedIds={this.handleSetSelectedIds}
                                    onSearchLines={this.handleSearchLines}
                                />
                            }
                        </>
                    }
                />
                {state.showSelectQualificationDialog &&
                    <SelectQualificationDialog
                        nodes={state.selectQualificationDialogFurtherEducations}
                        selectedIds={[]}
                        onOk={this.onSelectQualificationDialogOk}
                        onCancel={this.onSelectQualificationDialogCancel}
                    />
                }
            </>
        );
    }
}